import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ManualSection from '../ManualSection';
import './moderation-manual.scss';

/**
 * `/readme/moderation` manual content (TRIL-428), on the TRIL-424 framework.
 *
 * Content rule: explain the *process and outcomes*, not exploitable internals —
 * no moderator-assignment weighting, AI-triage thresholds, or tier tables. Set
 * expectations and reassure; don't hand out a playbook to abuse reporting or
 * dodge moderation. Report categories mirror the live ReportPostDialog (Safety
 * / Integrity / Legal / Quality); outcomes mirror the verdict system
 * (dismiss / rewrite / remove).
 */
export default function ModerationManual() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    el?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [hash]);

  return (
    <div className="MOD__MANUAL">
      <p className="MANUAL__LEAD">
        Moderation is how trilirivos keeps the community healthy — reviewing
        posts that people flag and acting only when something actually breaks
        the rules. There are two sides to it: <strong>reporting</strong> a post,
        and having your own post <strong>reported</strong>.
      </p>

      <section className="MOD__BLOCK" id="reporting">
        <h2>When you report a post</h2>
        <p className="MOD__SUB">
          Reporting is how you flag a post that breaks the rules. Pick the
          category that fits, add any context, and submit:
        </p>
        <ul className="MOD__CATS">
          <li>
            <b>Safety</b> — harassment, hate, sexual content, self-harm, child
            safety
          </li>
          <li>
            <b>Integrity</b> — unlabeled AI, impersonation, scams, spam
          </li>
          <li>
            <b>Legal</b> — IP theft, undisclosed ads, regulated goods
          </li>
          <li>
            <b>Quality</b> — graphic content, off-topic, spoilers
          </li>
        </ul>
        <p className="MOD__SUB">
          Your report goes to{' '}
          <b>review — a mix of automated checks and human moderators.</b> You
          get a quick confirmation, and reporting the same post twice won&apos;t
          double-count. Reports are how the community looks out for itself; use
          them in good faith.
        </p>
      </section>

      <section className="MOD__BLOCK" id="being-reported">
        <h2>When your post is reported</h2>
        <p className="MOD__SUB">
          A report <b>doesn&apos;t remove or hide your post</b> — it puts it in
          front of a reviewer, who decides what (if anything) happens. It does
          do one thing right away, though: on lower-severity reports, other
          readers may see a &ldquo;flagged, under review&rdquo; note while
          it&apos;s open (more on that below). If a reviewer reaches a decision,
          you&apos;re notified — here is what each one means.
        </p>
      </section>

      <section className="MOD__BLOCK" id="outcomes">
        <h2>The possible outcomes</h2>
        <p className="MOD__SUB">Open any one for what it means for you.</p>

        <ManualSection id="dismissed" title="Dismissed" defaultOpen>
          <p>
            The reviewer finds no problem. <b>Your post stays exactly as it
            was</b> — you&apos;ll just get a short note that it was reviewed.
          </p>
        </ManualSection>

        <ManualSection id="rewrite" title="Sent back for a rewrite">
          <p>
            The post is hidden from other readers and you get a banner
            explaining what needs to change. <b>It&apos;s not gone</b> — revise
            it and saving <b>republishes it automatically.</b> Think of this as
            the &ldquo;fix it&rdquo; outcome, not the &ldquo;you&apos;re in
            trouble&rdquo; one.
          </p>
        </ManualSection>

        <ManualSection id="removed" title="Removed">
          <p>
            The post is taken down and you&apos;re told why — which rule it
            broke and who reviewed it. If you believe it was a mistake, you can
            contact support or wait for an admin review.
          </p>
        </ManualSection>

        <div className="MOD__NOTE" id="community-flag">
          <span className="MOD__NOTE__MK">One more thing readers see</span>
          <p>
            While a lower-severity report is open, other readers may see a note
            that the post has been <b>flagged and is under review</b> (it names
            the concern and how many readers flagged it). It&apos;s context, not
            a verdict. It clears if the report is dismissed — but a moderator can
            also <b>confirm</b> it as a standing notice: your post stays up and
            nothing is removed, and the note stays on the post marked confirmed.
            A rewrite or removal replaces it with that outcome instead.
          </p>
        </div>
      </section>

      <div className="MOD__NOTE MOD__NOTE--reassure" id="reassurance">
        <span className="MOD__NOTE__MK">The short version</span>
        <p>
          Being reported isn&apos;t the same as being in trouble. A report
          starts a review — it doesn&apos;t remove or hide your post on its own;
          the rewrite path lets you fix and republish; and a removal always
          comes with a reason.
        </p>
      </div>

      <div className="MOD__NOTE MOD__NOTE--internal" id="under-the-hood">
        <span className="MOD__NOTE__MK">
          Why some of it stays under the hood
        </span>
        <p>
          We don&apos;t publish exactly how reports are routed or weighted —
          that keeps the system fair and hard to abuse, both by people gaming
          reports and by people trying to skirt the rules. The outcomes above
          are the part that actually affects you.
        </p>
      </div>
    </div>
  );
}
